import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Form, InputGroup, Spinner, Alert, Modal } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  fetchEventById,
  fetchRegistrations,
  updateRegistrationStatus,
  bulkUpdateStatus,
  updateAttendance,
  verifyPayment,
  verifyIdentity,
  clearRegistrations,
  EventRegistration,
  RegistrationStatus,
  AttendanceStatus,
  VerificationStatus,
} from '../redux/slices/eventsSlice';
import { eventsAPI } from '../services/eventsApi';

const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

// ─── Status helpers ───────────────────────────────────────────────────────────

const REG_STATUS: Record<RegistrationStatus, { label: string; color: string; bg: string }> = {
  pending:    { label: 'Pending',    color: '#92400e', bg: '#fef3c7' },
  approved:   { label: 'Approved',   color: '#065f46', bg: '#d1fae5' },
  declined:   { label: 'Declined',   color: '#991b1b', bg: '#fee2e2' },
  waitlisted: { label: 'Waitlisted', color: '#1e40af', bg: '#dbeafe' },
  cancelled:  { label: 'Cancelled',  color: '#374151', bg: '#f3f4f6' },
};

const ATT_STATUS: Record<AttendanceStatus, { label: string; color: string }> = {
  registered: { label: 'Registered', color: '#6366f1' },
  attended:   { label: 'Attended',   color: '#16a34a' },
  no_show:    { label: 'No Show',    color: '#dc2626' },
};

const VER_STATUS: Record<VerificationStatus, { label: string; color: string }> = {
  not_required: { label: '—',        color: '#94a3b8' },
  pending:      { label: 'Pending',  color: '#d97706' },
  verified:     { label: 'Verified', color: '#16a34a' },
  rejected:     { label: 'Rejected', color: '#dc2626' },
};

const Pill: React.FC<{ label: string; color: string; bg?: string }> = ({ label, color, bg }) => (
  <span style={{
    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
    color, background: bg || `${color}20`, whiteSpace: 'nowrap',
  }}>
    {label}
  </span>
);

// ─── Registration Detail Modal ────────────────────────────────────────────────

interface RegDetailModalProps {
  reg: EventRegistration;
  eventId: string;
  onClose: () => void;
  onRefresh: () => void;
}

const RegDetailModal: React.FC<RegDetailModalProps> = ({ reg, eventId, onClose, onRefresh }) => {
  const dispatch = useAppDispatch();
  const [reviewNotes, setReviewNotes] = useState(reg.review_notes || '');
  const [paymentNotes, setPaymentNotes] = useState(reg.payment_notes || '');
  const [verificationNotes, setVerificationNotes] = useState(reg.verification_notes || '');
  const [saving, setSaving] = useState(false);

  const handleStatusChange = async (status: RegistrationStatus) => {
    setSaving(true);
    try {
      await dispatch(updateRegistrationStatus({ eventId, regId: reg.id, data: { registration_status: status, review_notes: reviewNotes } })).unwrap();
      onRefresh();
    } finally { setSaving(false); }
  };

  const handleAttendance = async (status: AttendanceStatus) => {
    setSaving(true);
    try {
      await dispatch(updateAttendance({ eventId, regId: reg.id, attendance_status: status })).unwrap();
      onRefresh();
    } finally { setSaving(false); }
  };

  const handlePayment = async (verified: boolean) => {
    setSaving(true);
    try {
      await dispatch(verifyPayment({ eventId, regId: reg.id, data: { payment_verified: verified, payment_notes: paymentNotes } })).unwrap();
      onRefresh();
    } finally { setSaving(false); }
  };

  const handleIdentity = async (status: VerificationStatus) => {
    setSaving(true);
    try {
      await dispatch(verifyIdentity({ eventId, regId: reg.id, data: { verification_status: status, verification_notes: verificationNotes } })).unwrap();
      onRefresh();
    } finally { setSaving(false); }
  };

  const regMeta = REG_STATUS[reg.registration_status];
  const attMeta = ATT_STATUS[reg.attendance_status];
  const verMeta = VER_STATUS[reg.verification_status];

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: '1px solid #f1f5f9', padding: '16px 24px' }}>
        <div>
          <h5 style={{ fontWeight: 700, margin: 0, color: '#0f172a' }}>
            {reg.first_name} {reg.last_name}
          </h5>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{reg.email}</div>
        </div>
      </Modal.Header>
      <Modal.Body style={{ padding: '0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ padding: '16px 24px', borderRight: '1px solid #f1f5f9' }}>
            <label style={labelStyle}>Registration Status</label>
            <div style={{ marginBottom: 8 }}>
              <Pill label={regMeta.label} color={regMeta.color} bg={regMeta.bg} />
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(['pending','approved','declined','waitlisted','cancelled'] as RegistrationStatus[]).map(s => (
                <button
                  key={s}
                  onClick={() => handleStatusChange(s)}
                  disabled={saving || reg.registration_status === s}
                  style={{
                    fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                    border: reg.registration_status === s ? `2px solid ${REG_STATUS[s].color}` : '1px solid #e2e8f0',
                    background: reg.registration_status === s ? REG_STATUS[s].bg : '#fff',
                    color: REG_STATUS[s].color, fontWeight: 600,
                  }}
                >
                  {REG_STATUS[s].label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ padding: '16px 24px' }}>
            <label style={labelStyle}>Attendance</label>
            <div style={{ marginBottom: 8 }}>
              <Pill label={attMeta.label} color={attMeta.color} />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['registered','attended','no_show'] as AttendanceStatus[]).map(s => (
                <button
                  key={s}
                  onClick={() => handleAttendance(s)}
                  disabled={saving || reg.attendance_status === s}
                  style={{
                    fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                    border: reg.attendance_status === s ? `2px solid ${ATT_STATUS[s].color}` : '1px solid #e2e8f0',
                    background: reg.attendance_status === s ? `${ATT_STATUS[s].color}15` : '#fff',
                    color: ATT_STATUS[s].color, fontWeight: 600,
                  }}
                >
                  {ATT_STATUS[s].label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9' }}>
          <label style={labelStyle}>Review Notes</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <Form.Control
              value={reviewNotes}
              onChange={e => setReviewNotes(e.target.value)}
              placeholder="Internal notes for this registration..."
              style={{ fontSize: 13, border: '1px solid #e2e8f0' }}
            />
          </div>
        </div>

        {/* Payment section */}
        {reg.proof_of_payment_path && (
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', background: '#fffbf0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <label style={labelStyle}>Proof of Payment</label>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                color: reg.payment_verified ? '#065f46' : '#92400e',
                background: reg.payment_verified ? '#d1fae5' : '#fef3c7',
              }}>
                {reg.payment_verified ? '✓ Verified' : '⏳ Unverified'}
              </span>
            </div>
            <a
              href={`${API_URL}/${reg.proof_of_payment_path}`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', background: '#f8f9fc', borderRadius: 8,
                textDecoration: 'none', color: '#374151', fontSize: 13, marginBottom: 10,
              }}
            >
              📄 {reg.proof_of_payment_name || 'View Payment Proof'} ↗
            </a>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <Form.Control
                  value={paymentNotes}
                  onChange={e => setPaymentNotes(e.target.value)}
                  placeholder="Payment notes..."
                  style={{ fontSize: 13, border: '1px solid #e2e8f0' }}
                />
              </div>
              <button
                onClick={() => handlePayment(true)}
                disabled={saving || reg.payment_verified}
                style={{
                  background: '#d1fae5', border: 'none', borderRadius: 6, padding: '8px 14px',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#065f46',
                }}
              >
                ✓ Verify
              </button>
              <button
                onClick={() => handlePayment(false)}
                disabled={saving || !reg.payment_verified}
                style={{
                  background: '#fee2e2', border: 'none', borderRadius: 6, padding: '8px 14px',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#991b1b',
                }}
              >
                ✗ Reject
              </button>
            </div>
          </div>
        )}

        {/* Identity verification */}
        {(reg.id_document_path || reg.facial_image_path) && (
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', background: '#f0f9ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <label style={labelStyle}>Identity Verification</label>
              <Pill label={verMeta.label} color={verMeta.color} />
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {reg.id_document_path && (
                <a
                  href={`${API_URL}/${reg.id_document_path}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: '#fff', borderRadius: 8, textDecoration: 'none', color: '#374151', fontSize: 12, border: '1px solid #e2e8f0' }}
                >
                  🪪 {reg.id_document_name || 'ID Document'} ↗
                </a>
              )}
              {reg.facial_image_path && (
                <div
                  onClick={() => window.open(`${API_URL}/${reg.facial_image_path}`, '_blank')}
                  style={{ cursor: 'pointer', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}
                >
                  <img
                    src={`${API_URL}/${reg.facial_image_path}`}
                    alt="Facial"
                    style={{ height: 60, width: 60, objectFit: 'cover', display: 'block' }}
                  />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <Form.Control
                  value={verificationNotes}
                  onChange={e => setVerificationNotes(e.target.value)}
                  placeholder="Verification notes..."
                  style={{ fontSize: 13, border: '1px solid #e2e8f0' }}
                />
              </div>
              <button
                onClick={() => handleIdentity('verified')}
                disabled={saving || reg.verification_status === 'verified'}
                style={{ background: '#d1fae5', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#065f46' }}
              >
                ✓ Verify
              </button>
              <button
                onClick={() => handleIdentity('rejected')}
                disabled={saving || reg.verification_status === 'rejected'}
                style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#991b1b' }}
              >
                ✗ Reject
              </button>
            </div>
          </div>
        )}

        {/* Registrant info */}
        <div style={{ padding: '16px 24px' }}>
          <label style={labelStyle}>Registrant Info</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
            {[
              { label: 'Email', value: reg.email },
              { label: 'Phone', value: reg.phone || '—' },
              { label: 'Registered', value: new Date(reg.created_at).toLocaleString() },
              { label: 'Reviewer', value: reg.reviewer_name || '—' },
            ].map(item => (
              <div key={item.label}>
                <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', fontWeight: 600, marginBottom: 2 }}>{item.label}</div>
                <div style={{ color: '#1e293b', fontWeight: 500 }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const EventRegistrationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { currentEvent, registrations, registrationPagination, registrationsLoading } = useAppSelector(s => s.events);

  const [search, setSearch] = useState('');
  const [regFilter, setRegFilter] = useState('');
  const [attFilter, setAttFilter] = useState('');
  const [verFilter, setVerFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [modalReg, setModalReg] = useState<EventRegistration | null>(null);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    dispatch(fetchRegistrations({
      eventId: id,
      params: {
        search: search || undefined,
        registration_status: regFilter || undefined,
        attendance_status: attFilter as AttendanceStatus || undefined,
        verification_status: verFilter as VerificationStatus || undefined,
        page,
        limit: 50,
      },
    }));
  }, [dispatch, id, search, regFilter, attFilter, verFilter, page]);

  useEffect(() => {
    if (id) dispatch(fetchEventById(id));
    return () => { dispatch(clearRegistrations()); };
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (regId: string) => {
    const next = new Set(selectedIds);
    if (next.has(regId)) next.delete(regId);
    else next.add(regId);
    setSelectedIds(next);
  };

  const toggleAll = () => {
    if (selectedIds.size === registrations.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(registrations.map(r => r.id)));
  };

  const handleBulkUpdate = async () => {
    if (!id || !bulkStatus || !selectedIds.size) return;
    setBulkSaving(true);
    try {
      await dispatch(bulkUpdateStatus({
        eventId: id,
        data: { registration_ids: Array.from(selectedIds), registration_status: bulkStatus, review_notes: bulkNotes },
      })).unwrap();
      setSelectedIds(new Set());
      setBulkStatus('');
    } finally { setBulkSaving(false); }
  };

  const handleExport = async () => {
    if (!id || !currentEvent) return;
    setExportLoading(true);
    try {
      const res = await eventsAPI.exportRegistrations(id, regFilter || undefined);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentEvent.slug}-registrations.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setExportLoading(false); }
  };

  const stats = {
    total: registrationPagination.total,
    pending: registrations.filter(r => r.registration_status === 'pending').length,
    approved: registrations.filter(r => r.registration_status === 'approved').length,
    attended: registrations.filter(r => r.attendance_status === 'attended').length,
  };

  return (
    <div style={{ backgroundColor: '#f8f9fc', minHeight: '100vh', paddingTop: 28, paddingBottom: 60 }}>
      <Container fluid style={{ maxWidth: 1300 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(`/events/${id}`)}
              style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 13px', cursor: 'pointer', fontSize: 13, color: '#64748b' }}
            >
              ← Event
            </button>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 1 }}>
                Registrations
              </h1>
              {currentEvent && (
                <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{currentEvent.title}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleExport}
            disabled={exportLoading}
            style={{
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
              padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#374151', fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {exportLoading ? <Spinner animation="border" size="sm" /> : '⬇'} Export CSV
          </button>
        </div>

        {/* Stats strip */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total', value: registrationPagination.total, color: '#00579a' },
            { label: 'Pending', value: registrations.filter(r => r.registration_status === 'pending').length, color: '#d97706' },
            { label: 'Approved', value: registrations.filter(r => r.registration_status === 'approved').length, color: '#16a34a' },
            { label: 'Declined', value: registrations.filter(r => r.registration_status === 'declined').length, color: '#dc2626' },
            { label: 'Attended', value: registrations.filter(r => r.attendance_status === 'attended').length, color: '#6366f1' },
          ].map(s => (
            <div key={s.label} style={{
              flex: 1, background: '#fff', borderRadius: 10, padding: '12px 16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center',
            }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{
          background: '#fff', borderRadius: 10, padding: '14px 18px', marginBottom: 12,
          display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <InputGroup style={{ maxWidth: 260 }}>
            <InputGroup.Text style={{ background: '#f8f9fc', border: '1px solid #e2e8f0', borderRight: 'none', fontSize: 13 }}>🔍</InputGroup.Text>
            <Form.Control
              placeholder="Search name or email..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{ border: '1px solid #e2e8f0', borderLeft: 'none', fontSize: 13 }}
            />
          </InputGroup>

          <Form.Select
            value={regFilter}
            onChange={e => { setRegFilter(e.target.value); setPage(1); }}
            style={{ maxWidth: 160, fontSize: 13, border: '1px solid #e2e8f0' }}
          >
            <option value="">All Statuses</option>
            {Object.entries(REG_STATUS).map(([v, m]) => (
              <option key={v} value={v}>{m.label}</option>
            ))}
          </Form.Select>

          <Form.Select
            value={attFilter}
            onChange={e => { setAttFilter(e.target.value); setPage(1); }}
            style={{ maxWidth: 160, fontSize: 13, border: '1px solid #e2e8f0' }}
          >
            <option value="">All Attendance</option>
            {Object.entries(ATT_STATUS).map(([v, m]) => (
              <option key={v} value={v}>{m.label}</option>
            ))}
          </Form.Select>

          <Form.Select
            value={verFilter}
            onChange={e => { setVerFilter(e.target.value); setPage(1); }}
            style={{ maxWidth: 160, fontSize: 13, border: '1px solid #e2e8f0' }}
          >
            <option value="">All Verification</option>
            {Object.entries(VER_STATUS).map(([v, m]) => (
              <option key={v} value={v}>{m.label}</option>
            ))}
          </Form.Select>
        </div>

        {/* Bulk actions */}
        {selectedIds.size > 0 && (
          <div style={{
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
            padding: '12px 18px', marginBottom: 12,
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>
              {selectedIds.size} selected
            </span>
            <Form.Select
              value={bulkStatus}
              onChange={e => setBulkStatus(e.target.value)}
              style={{ maxWidth: 160, fontSize: 13, border: '1px solid #bfdbfe' }}
            >
              <option value="">Change status to...</option>
              {Object.entries(REG_STATUS).map(([v, m]) => (
                <option key={v} value={v}>{m.label}</option>
              ))}
            </Form.Select>
            <Form.Control
              value={bulkNotes}
              onChange={e => setBulkNotes(e.target.value)}
              placeholder="Optional notes..."
              style={{ maxWidth: 200, fontSize: 13, border: '1px solid #bfdbfe' }}
            />
            <button
              onClick={handleBulkUpdate}
              disabled={!bulkStatus || bulkSaving}
              style={{
                background: '#1e40af', color: '#fff', border: 'none', borderRadius: 6,
                padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {bulkSaving ? 'Saving...' : 'Apply'}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              style={{ background: 'none', border: 'none', fontSize: 13, cursor: 'pointer', color: '#64748b' }}
            >
              Clear
            </button>
          </div>
        )}

        {/* Table */}
        {registrationsLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spinner animation="border" style={{ color: '#00579a' }} />
          </div>
        ) : registrations.length === 0 ? (
          <div style={{
            background: '#fff', borderRadius: 10, padding: '60px 20px', textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>👥</div>
            <p style={{ color: '#94a3b8' }}>No registrations found</p>
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fc' }}>
                  <th style={thStyle}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === registrations.length && registrations.length > 0}
                      onChange={toggleAll}
                    />
                  </th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Attendance</th>
                  <th style={thStyle}>Verification</th>
                  <th style={thStyle}>Payment</th>
                  <th style={thStyle}>Registered</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((reg, i) => {
                  const regMeta = REG_STATUS[reg.registration_status];
                  const attMeta = ATT_STATUS[reg.attendance_status];
                  const verMeta = VER_STATUS[reg.verification_status];

                  return (
                    <tr
                      key={reg.id}
                      style={{
                        borderTop: '1px solid #f1f5f9',
                        background: selectedIds.has(reg.id) ? '#f0f7ff' : (i % 2 === 0 ? '#fff' : '#fafafa'),
                        cursor: 'pointer',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => { if (!selectedIds.has(reg.id)) e.currentTarget.style.background = '#f8f9fc'; }}
                      onMouseLeave={e => { if (!selectedIds.has(reg.id)) e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'; }}
                    >
                      <td style={tdStyle} onClick={e => { e.stopPropagation(); toggleSelect(reg.id); }}>
                        <input type="checkbox" checked={selectedIds.has(reg.id)} onChange={() => toggleSelect(reg.id)} />
                      </td>
                      <td style={tdStyle} onClick={() => setModalReg(reg)}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                          {reg.first_name} {reg.last_name}
                        </span>
                        {reg.phone && <div style={{ fontSize: 11, color: '#94a3b8' }}>{reg.phone}</div>}
                      </td>
                      <td style={tdStyle} onClick={() => setModalReg(reg)}>
                        <span style={{ fontSize: 13, color: '#374151' }}>{reg.email}</span>
                      </td>
                      <td style={tdStyle} onClick={() => setModalReg(reg)}>
                        <Pill label={regMeta.label} color={regMeta.color} bg={regMeta.bg} />
                      </td>
                      <td style={tdStyle} onClick={() => setModalReg(reg)}>
                        <span style={{ fontSize: 12, color: attMeta.color, fontWeight: 600 }}>{attMeta.label}</span>
                      </td>
                      <td style={tdStyle} onClick={() => setModalReg(reg)}>
                        <span style={{ fontSize: 12, color: verMeta.color, fontWeight: 600 }}>{verMeta.label}</span>
                      </td>
                      <td style={tdStyle} onClick={() => setModalReg(reg)}>
                        {reg.proof_of_payment_path ? (
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                            color: reg.payment_verified ? '#065f46' : '#92400e',
                            background: reg.payment_verified ? '#d1fae5' : '#fef3c7',
                          }}>
                            {reg.payment_verified ? '✓ Verified' : '⏳ Pending'}
                          </span>
                        ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
                      </td>
                      <td style={tdStyle} onClick={() => setModalReg(reg)}>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>
                          {new Date(reg.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => setModalReg(reg)}
                          style={{
                            background: '#f1f5f9', border: 'none', borderRadius: 6,
                            padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: '#374151', fontWeight: 600,
                          }}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {registrationPagination.pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 24 }}>
            {Array.from({ length: registrationPagination.pages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  width: 34, height: 34, borderRadius: 8, border: 'none',
                  background: p === page ? '#00579a' : '#fff',
                  color: p === page ? '#fff' : '#374151',
                  fontWeight: p === page ? 700 : 400,
                  cursor: 'pointer', fontSize: 13,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                }}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </Container>

      {/* Registration detail modal */}
      {modalReg && id && (
        <RegDetailModal
          reg={modalReg}
          eventId={id}
          onClose={() => setModalReg(null)}
          onRefresh={() => {
            load();
            setModalReg(prev => {
              if (!prev) return null;
              const updated = registrations.find(r => r.id === prev.id);
              return updated || null;
            });
          }}
        />
      )}
    </div>
  );
};

// ─── Shared styles ────────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: 11,
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  textAlign: 'left',
  letterSpacing: '0.5px',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 14px',
  verticalAlign: 'middle',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  display: 'block',
  marginBottom: 6,
};

export default EventRegistrationsPage;
